/**
 * Work out what a configured video URL actually points at.
 *
 * The Watch section lets an administrator paste any URL into HomePageVideo.
 * Two very different things get pasted: a single video, which should play
 * inside the banner, and a channel or playlist page - Parliament of Uganda's
 * own site links to their channel, not to one clip - which cannot be embedded
 * at all. Handing a channel URL to the player renders an empty black frame,
 * so those open on YouTube instead.
 */
export type VideoUrlKind = 'embeddable' | 'external' | 'none';

// Forms that identify one playable item. Everything else on youtube.com is a
// destination page: /@handle, /channel/, /c/, /user/, /playlist, /videos.
const YOUTUBE_VIDEO_PATTERNS = [
  /youtube\.com\/watch\?(?:.*&)?v=[\w-]+/i,
  /youtu\.be\/[\w-]+/i,
  /youtube\.com\/(?:embed|shorts|live|v)\/[\w-]+/i,
];

const NON_YOUTUBE_EMBEDDABLE = [
  /vimeo\.com\/\d+/i,
  /(?:facebook\.com|fb\.watch)\/.+\/videos?\//i,
  /\.(?:mp4|webm|ogv|m3u8)(?:\?|$)/i,
];

export function videoUrlKind(url?: string | null): VideoUrlKind {
  const value = (url || '').trim();
  if (!value) return 'none';

  // [/.] so both "//youtu.be/x" and "www.youtube.com/x" match, without
  // "notyoutube.com" doing so.
  const isYouTube = /(?:^|[/.])(?:youtube\.com|youtu\.be)\//i.test(value);
  if (isYouTube) {
    return YOUTUBE_VIDEO_PATTERNS.some((p) => p.test(value)) ? 'embeddable' : 'external';
  }

  return NON_YOUTUBE_EMBEDDABLE.some((p) => p.test(value)) ? 'embeddable' : 'external';
}

/**
 * Turn a YouTube link into its /embed/ form.
 *
 * An administrator pastes whatever the address bar or the Share button gave
 * them - watch?v=, youtu.be, or already-embed - and only the embed form loads
 * in an iframe. Returns null for anything that is not a single YouTube video.
 */
export function toYouTubeEmbedUrl(url?: string | null): string | null {
  const value = (url || '').trim();
  if (!value) return null;

  const id = value.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|live\/|watch\?(?:.*&)?v=))([\w-]{11})/i,
  )?.[1];

  return id ? `https://www.youtube.com/embed/${id}` : null;
}
